// src/main.tsx
import { MantineProvider, createTheme, localStorageColorSchemeManager } from '@mantine/core';
import '@mantine/core/styles.css';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import { InconnectProvider } from '@inconnect/react';
import '@inconnect/react/styles.css';
import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import './styles/themes.css'; // global theme CSS
import './styles/careamy-green.css'; // unified Doctor Portal visual theme
import { applyThemeColor, themeColor, themeColorConfigured, themeColorRaw, themePalette } from './theme/themeColor';
import { inconnect } from './inconnectClient';
import { LanguageProvider } from './context/LanguageContext';
import './i18n'; // (optional – if you plan to use i18next later)

// ---- Prime color scheme from URL (runs before render) ----
(function primeColorSchemeFromUrl() {
  try {
    const KEY = 'careamy-color-scheme';
    const sp = new URLSearchParams(window.location.search);
    const raw = (sp.get('colorScheme') || sp.get('theme') || sp.get('mode') || '').trim().toLowerCase();
    const darkFlag = (sp.get('dark') || '').trim().toLowerCase(); // supports ?dark=1 / true

    let chosen: 'light' | 'dark' | 'auto' | '' = '';
    if (raw === 'dark' || raw === 'light' || raw === 'auto') {
      chosen = raw as any;
    } else if (darkFlag === '1' || darkFlag === 'true') {
      chosen = 'dark';
    }

    if (chosen) {
      localStorage.setItem(KEY, chosen);
      if (chosen === 'light' || chosen === 'dark') {
        document.documentElement.setAttribute('data-mantine-color-scheme', chosen);
      } else {
        document.documentElement.removeAttribute('data-mantine-color-scheme');
      }
    } else {
      if (!localStorage.getItem(KEY)) {
        localStorage.setItem(KEY, 'light');
        document.documentElement.setAttribute('data-mantine-color-scheme', 'light');
      }
    }
  } catch {
    /* no-op */
  }
})();
// ---------------------------------------------------------

applyThemeColor();
if (import.meta.env.DEV) {
  console.info(`[CareAmy theme] VITE_THEME_COLOR=${themeColorRaw || '<unset>'}; resolved=${themeColor}; valid=${themeColorConfigured}; mode=${import.meta.env.MODE}`);
  if (!themeColorConfigured) {
    console.warn('[CareAmy theme] VITE_THEME_COLOR is missing/invalid. Put e.g. VITE_THEME_COLOR=#0D9488 in the project-root .env (next to package.json), restart Vite, and reload the page.');
  }
}

const theme = createTheme({
  primaryShade: { light: 6, dark: 7 },
  white: '#ffffff',
  primaryColor: 'careamy',
  defaultRadius: 'md',
  fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
  colors: {
    careamy: themePalette,
    green: ['#effaf3','#dcf4e4','#bce9cf','#91d8b1','#5fc58e','#32ae70','#198c57','#127348','#105a3b','#0b432d'],
    gray: [
      '#f8f9fb',
      '#f3f4f6',
      '#e9ecef',
      '#dde1e6',
      '#cfd4dc',
      '#b8c0cc',
      '#8b95a5',
      '#667085',
      '#475467',
      '#344054',
    ],
  },
  headings: { sizes: { h1: { fontSize: '1.125rem', fontWeight: '500', lineHeight: '2.0' } } },
  fontSizes: { xs: '0.6875rem', sm: '0.875rem', md: '0.875rem', lg: '1.0rem', xl: '1.125rem' },
});

const colorSchemeManager = localStorageColorSchemeManager({ key: 'careamy-color-scheme' });


const isPublicPreRegisterSummaryPath = /^\/pre-register\/[^/]+\/summary\/?$/.test(window.location.pathname);
const NormalDoctorPortalApp = lazy(() => import('./App').then((module) => ({ default: module.App })));
const PublicPreRegisterSummaryRoute = lazy(() => import('./routes/PublicPreRegisterSummaryRoute'));

function EntryPoint(): React.JSX.Element {
  if (isPublicPreRegisterSummaryPath) {
    // Do not force an already-authenticated doctor through the restricted
    // AuthService facade. The authenticated portal can write Communication
    // directly and use the inconnect WebSocket subscription. A secure external
    // launch (authCode present) must always stay on the restricted route.
    const hasAuthCode = new URL(window.location.href).searchParams.has('authCode');
    const hasinconnectSession = Boolean(inconnect.getAccessToken?.());

    if (hasAuthCode || !hasinconnectSession) {
      return (
        <Routes>
          <Route path="/pre-register/:patientId/summary" element={<PublicPreRegisterSummaryRoute />} />
        </Routes>
      );
    }
  }
  return <NormalDoctorPortalApp />;
}

const root = createRoot(document.getElementById('root') as HTMLDivElement);

root.render(
  <StrictMode>
    <BrowserRouter>
      <InconnectProvider inconnect={inconnect}>
        <MantineProvider
          theme={theme}
          defaultColorScheme="light"
          colorSchemeManager={colorSchemeManager}
        >
          <Notifications />
          {/* ✅ Wrap App inside LanguageProvider */}
          <LanguageProvider>
            <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>}>
              <EntryPoint />
            </Suspense>
          </LanguageProvider>
        </MantineProvider>
      </InconnectProvider>
    </BrowserRouter>
  </StrictMode>
);
